/**
 * Chat Button Component
 * Floating button to open chat widget
 * Following UI reference design with soft, modern styling
 *
 * Accessibility:
 * - Meets 44px minimum touch target on mobile
 * - Respects prefers-reduced-motion
 * - Proper ARIA labeling
 */

import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { MessageSquare } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { useReducedMotion } from '../../../../shared/hooks/useReducedMotion';

interface ChatButtonProps {
  onClick: () => void;
  isOpen: boolean;
  className?: string;
  badgeCount?: number;
}

/**
 * Floating chat button component
 * Positioned bottom-right, follows UI reference design
 */
export const ChatButton: React.FC<ChatButtonProps> = ({
  onClick,
  isOpen,
  className,
  badgeCount = 0
}) => {
  const { prefersReducedMotion, spring } = useReducedMotion();

  return (
    <AnimatePresence>
      {!isOpen && (
        <motion.button
          initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0, y: 20 }}
          animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
          exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0, y: 20 }}
          transition={spring}
          whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
          whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClick();
          }}
          className={cn(
            // Positioning
            'fixed bottom-6 right-6 z-50',
            'pointer-events-auto',

            // Size - 56px base, meets 44px minimum touch target
            'w-14 h-14 min-w-[56px] min-h-[56px]',
            'rounded-2xl',

            // Colors - consistent emerald primary
            'bg-emerald-600 hover:bg-emerald-700',
            'text-white',

            // Effects
            'shadow-lg hover:shadow-xl',
            'flex items-center justify-center',
            'transition-colors duration-200',

            // Focus state - high visibility
            'focus:outline-hidden focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2',
            'focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2',

            // Mobile adjustments - slightly smaller position offset
            'max-md:bottom-4 max-md:right-4',

            className
          )}
          aria-label={badgeCount > 0
            ? `Open chat assistant. ${badgeCount} unread message${badgeCount > 1 ? 's' : ''}`
            : 'Open Hajra chat assistant'
          }
          aria-haspopup="dialog"
          type="button"
        >
          {/* Badge for unread messages */}
          {badgeCount > 0 && (
            <motion.div
              initial={prefersReducedMotion ? { opacity: 0 } : { scale: 0 }}
              animate={prefersReducedMotion ? { opacity: 1 } : { scale: 1 }}
              className={cn(
                'absolute -top-1 -right-1',
                'w-5 h-5 min-w-[20px] min-h-[20px]',
                'bg-red-500 rounded-full',
                'flex items-center justify-center',
                'text-xs font-bold text-white',
                'shadow-md'
              )}
              aria-hidden="true"
            >
              {badgeCount > 9 ? '9+' : badgeCount}
            </motion.div>
          )}

          <MessageSquare className="w-6 h-6" aria-hidden="true" />
        </motion.button>
      )}
    </AnimatePresence>
  );
};

export default ChatButton;
